/**
 * @fileoverview useBudgetHandlers - Budget creation and invite acceptance handlers.
 *
 * Provides handlers for budget lifecycle events that require Auth0 tokens and
 * user state refresh. These operations are triggered from the UI but require
 * orchestration of multiple API calls.
 *
 * @description This hook provides:
 * - Budget creation with automatic month initialization
 * - Invite acceptance with budget selection
 * - Auth0 token management
 * - User state refresh after budget changes
 *
 * Budget creation flow:
 * 1. Create budget via API (gets budgetKey)
 * 2. Initialize months for the budget
 * 3. Refresh user state to load new budget list
 *
 * Invite acceptance flow:
 * 1. Accept invite (handled elsewhere)
 * 2. Save accepted budget to localStorage
 * 3. Refresh user state to load updated budget list
 *
 * @module hooks/useBudgetHandlers
 */
import { useCallback } from 'react';

/**
 * Hook for budget lifecycle event handlers.
 *
 * @param {Function} getAccessTokenSilently - Auth0 token retrieval function
 * @param {Object} authUser - Auth user hook with refreshUser method
 * @returns {Object} Budget handlers
 * @returns {Function} return.handleBudgetCreated - Handle budget creation completion
 * @returns {Function} return.handleInviteAccepted - Handle invite acceptance completion
 *
 * @example
 * const handlers = useBudgetHandlers(getAccessTokenSilently, authUser);
 * await handlers.handleBudgetCreated({ budgetName: 'Family', startMonth: '2025-01' });
 * await handlers.handleInviteAccepted(acceptedBudget);
 */
export const useBudgetHandlers = (getAccessTokenSilently, authUser) => {
  // Handler for creating a new budget
  const handleBudgetCreated = useCallback(async ({ budgetName, startMonth, singleBankMode }) => {
    try {
      const token = await getAccessTokenSilently();
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_BASE_URL}/budgets/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ budgetName, startMonth, singleBankMode })
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create budget');
        } else {
          await response.text();
          throw new Error('Server error occurred. Check backend logs.');
        }
      }

      const result = await response.json();
      const budgetKey = result.budget.budgetKey;

      await fetch(`${API_BASE_URL}/budgets/${budgetKey}/initialize-months`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      // Save the new budget key to localStorage so it gets selected after refresh
      localStorage.setItem('selectedBudgetKey', budgetKey);

      await authUser.refreshUser();
    } catch (error) {
      throw error;
    }
  }, [getAccessTokenSilently, authUser]);

  // Handler for accepting an invite
  const handleInviteAccepted = useCallback(async (acceptedBudget) => {
    if (acceptedBudget && acceptedBudget.budgetKey) {
      localStorage.setItem('selectedBudgetKey', acceptedBudget.budgetKey);
    }
    await authUser.refreshUser();
  }, [authUser]);

  return {
    handleBudgetCreated,
    handleInviteAccepted
  };
};
